import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCategoryLevels } from '../hooks/useCategoryLevels';
import './CategoryLevelsCard.css';

const SEEN_KEY = 'categoryLevels:seen:';

function readSeen(studentId) {
  try {
    return localStorage.getItem(SEEN_KEY + studentId) === '1';
  } catch {
    return false;
  }
}

function writeSeen(studentId, seen) {
  try {
    if (seen) localStorage.setItem(SEEN_KEY + studentId, '1');
    else localStorage.removeItem(SEEN_KEY + studentId);
  } catch {
    // Private mode / quota — the hint is an optimisation, never load-bearing.
  }
}

/** Holds the fetch back until the browser is idle so it never races the dashboard's own requests. */
function useIdle() {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (typeof requestIdleCallback !== 'function') {
      const timer = setTimeout(() => setIdle(true), 300);
      return () => clearTimeout(timer);
    }
    const handle = requestIdleCallback(() => setIdle(true), { timeout: 2000 });
    return () => cancelIdleCallback(handle);
  }, []);
  return idle;
}

/**
 * Dashboard entry point for the daily "categories" nudge (Family/Activities/
 * Countries word levels). Links to the circles page so the student sees every
 * theme at a glance and picks which to specialize in today, rather than the
 * card itself deciding for them.
 *
 * Renders nothing at all unless the server says this student has the track
 * enabled and hasn't done a level today, so students outside the rollout see
 * no change.
 *
 * @param {{ studentId: string }} props
 */
export function CategoryLevelsCard({ studentId }) {
  const idle = useIdle();
  const { data, enabled, completedToday } = useCategoryLevels(idle ? studentId : null);

  // Students who saw the card last time get its space reserved up front, so
  // the assignment list below does not jump when the deferred fetch lands.
  const [expected] = useState(() => readSeen(studentId));

  const show = enabled && !completedToday;

  useEffect(() => {
    if (data) writeSeen(studentId, show);
  }, [data, show, studentId]);

  if (!show) {
    return expected && !data ? <div className="cl-card-placeholder" aria-hidden="true" /> : null;
  }

  return (
    <Link to={`/categories/${studentId}`} className="cl-card">
      <span className="cl-card-icon" aria-hidden="true">🗂️</span>
      <span className="cl-card-info">
        <span className="cl-card-title">今日词汇挑战</span>
        <span className="cl-card-secondary">3 个主题 · 挑一个今天专攻</span>
      </span>
      <span className="cl-card-go" aria-hidden="true">▶</span>
    </Link>
  );
}

export default CategoryLevelsCard;
